import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { assetService } from "../../../../services/api/assetService";
import {
  buildAssetExportParams,
  buildAssetDateParams,
  validateAssetDateRange,
} from "../utils/assetFilterUtils";
import { mapOriginalProfilesToAssetRows } from "../utils/assetFormatters";
import { downloadPdfBlob } from "../utils/downloadPdfBlob";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

const parsePositiveNumber = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const buildAssetParams = ({ page, limit, search, fromDate, toDate }) => {
  const params = { page, limit };
  const dateParams = buildAssetDateParams({ fromDate, toDate });
  const keyword = search.trim();

  if (dateParams.fromDate) params.fromDate = dateParams.fromDate;
  if (dateParams.toDate) params.toDate = dateParams.toDate;
  if (keyword) params.search_value = keyword;

  return params;
};

export const useAssetManagement = ({ enabled = true } = {}) => {
  const { token_client } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestIdRef = useRef(0);

  const page = parsePositiveNumber(searchParams.get("page"), DEFAULT_PAGE);
  const querySearch =
    searchParams.get("search_value") || searchParams.get("search") || "";
  const queryFromDate = searchParams.get("fromDate") || "";
  const queryToDate = searchParams.get("toDate") || "";

  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIMIT);
  const [search, setSearch] = useState(querySearch);
  const [appliedSearch, setAppliedSearch] = useState(querySearch);
  const [fromDate, setFromDate] = useState(queryFromDate);
  const [toDate, setToDate] = useState(queryToDate);
  const [appliedFromDate, setAppliedFromDate] = useState(queryFromDate);
  const [appliedToDate, setAppliedToDate] = useState(queryToDate);
  const [searchApplyCount, setSearchApplyCount] = useState(0);
  const [assets, setAssets] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: page,
    totalPages: 1,
    totalProfiles: 0,
    limit: DEFAULT_LIMIT,
  });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setSearch(querySearch);
    setAppliedSearch(querySearch);
    setFromDate(queryFromDate);
    setToDate(queryToDate);
    setAppliedFromDate(queryFromDate);
    setAppliedToDate(queryToDate);
  }, [queryFromDate, querySearch, queryToDate]);

  const fetchAssets = useCallback(async () => {
    if (!enabled) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    try {
      setLoading(true);
      setError("");

      const response = await assetService.getOriginalProfiles(
        token_client,
        buildAssetParams({
          page,
          limit: itemsPerPage,
          search: appliedSearch,
          fromDate: appliedFromDate,
          toDate: appliedToDate,
        }),
      );

      if (requestIdRef.current !== requestId) return;

      const responsePagination = response?.pagination || {};
      const currentPage = parsePositiveNumber(
        responsePagination.currentPage,
        page,
      );
      const limit = parsePositiveNumber(responsePagination.limit, itemsPerPage);

      setAssets(
        mapOriginalProfilesToAssetRows(response?.data || [], currentPage, limit),
      );
      setPagination({
        currentPage,
        totalPages: parsePositiveNumber(responsePagination.totalPages, 1),
        totalProfiles: Number(responsePagination.totalProfiles) || 0,
        limit,
      });
    } catch (err) {
      if (requestIdRef.current !== requestId) return;

      console.error("Error fetching original asset profiles:", err);
      setAssets([]);
      setPagination({
        currentPage: DEFAULT_PAGE,
        totalPages: 1,
        totalProfiles: 0,
        limit: itemsPerPage,
      });
      setError("Không thể tải danh sách tài sản. Vui lòng thử lại.");
      toast.dismiss();
      toast.error("Lỗi khi lấy danh sách tài sản");
    } finally {
      if (requestIdRef.current === requestId) {
        setLoading(false);
        setHasLoadedOnce(true);
      }
    }
  }, [
    appliedFromDate,
    appliedSearch,
    appliedToDate,
    enabled,
    itemsPerPage,
    page,
    token_client,
  ]);

  useEffect(() => {
    if (enabled) fetchAssets();
  }, [enabled, fetchAssets, searchApplyCount]);

  const handleSearch = useCallback(() => {
    const dateError = validateAssetDateRange(fromDate, toDate);

    if (dateError) {
      toast.error(dateError);
      return;
    }

    const nextParams = new URLSearchParams();
    nextParams.set("page", String(DEFAULT_PAGE));

    const keyword = search.trim();
    setAppliedSearch(keyword);
    setSearch(keyword);
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
    setSearchApplyCount((count) => count + 1);

    if (keyword) nextParams.set("search_value", keyword);
    if (fromDate) nextParams.set("fromDate", fromDate);
    if (toDate) nextParams.set("toDate", toDate);

    setSearchParams(nextParams);
  }, [fromDate, search, setSearchParams, toDate]);

  const handlePageChange = useCallback(
    (nextPage) => {
      const normalizedPage = Math.min(
        Math.max(parsePositiveNumber(nextPage, DEFAULT_PAGE), DEFAULT_PAGE),
        pagination.totalPages || DEFAULT_PAGE,
      );
      if (normalizedPage === pagination.currentPage) return;

      const nextParams = new URLSearchParams(searchParams.toString());
      nextParams.set("page", String(normalizedPage));
      setSearchParams(nextParams);
    },
    [pagination.currentPage, pagination.totalPages, searchParams, setSearchParams],
  );

  const handleItemsPerPageChange = useCallback(
    (nextLimit) => {
      setItemsPerPage(nextLimit);
      const nextParams = new URLSearchParams(searchParams.toString());
      nextParams.set("page", String(DEFAULT_PAGE));
      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams],
  );

  const handleExportPdf = useCallback(async () => {
    if (exporting) return;

    const dateError = validateAssetDateRange(appliedFromDate, appliedToDate);

    if (dateError) {
      toast.error(dateError);
      return;
    }

    try {
      setExporting(true);

      const response = await assetService.exportOriginalProfilesPdf(
        token_client,
        buildAssetExportParams({
          search: appliedSearch,
          fromDate: appliedFromDate,
          toDate: appliedToDate,
        }),
      );

      downloadPdfBlob(response, `Danh-muc-tai-san-tri-tue-${Date.now()}.pdf`);
      toast.success("Xuất file PDF thành công!");
    } catch (err) {
      console.error("Error exporting original asset profiles PDF:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Xuất file PDF thất bại",
      );
    } finally {
      setExporting(false);
    }
  }, [
    appliedFromDate,
    appliedSearch,
    appliedToDate,
    exporting,
    token_client,
  ]);

  return useMemo(
    () => ({
      appliedFromDate,
      appliedToDate,
      assets,
      currentPage: pagination.currentPage,
      error,
      exporting,
      fromDate,
      hasLoadedOnce,
      itemsPerPage,
      loading,
      search,
      toDate,
      totalPages: pagination.totalPages,
      totalProfiles: pagination.totalProfiles,
      fetchAssets,
      handleExportPdf,
      handleItemsPerPageChange,
      handlePageChange,
      handleSearch,
      setFromDate,
      setSearch,
      setToDate,
    }),
    [
      appliedFromDate,
      appliedToDate,
      assets,
      error,
      exporting,
      fetchAssets,
      fromDate,
      handleExportPdf,
      handleItemsPerPageChange,
      handlePageChange,
      handleSearch,
      hasLoadedOnce,
      itemsPerPage,
      loading,
      pagination.currentPage,
      pagination.totalPages,
      pagination.totalProfiles,
      search,
      toDate,
    ],
  );
};
